/**
 * EmbeddedDoc — render a TechDocs page inline anywhere in the app.
 *
 * Use this whenever a UI surface (tab, panel, modal, sidebar) wants to show
 * documentation that lives in this repo's `docs/` directory (or another
 * entity's TechDocs). The pattern was originally extracted from the Blueprints
 * Authoring Guide tab; it generalises to any "bring docs to the user" use
 * case.
 *
 * ## Why use this instead of the raw TechDocs components
 *
 * `<TechDocsReaderPage>` + `<TechDocsReaderPageContent>` requires three
 * things to embed correctly: a custom render function (to skip the default
 * page header), `defaultPath` with a leading slash (to override URL-based
 * routing), and a stable `entityRef`. This component bundles those defaults
 * so callers only need to specify the docs path.
 *
 * ## Where the docs live
 *
 * The repo root has `catalog-info.yaml` declaring `component:default/backstage`
 * with `backstage.io/techdocs-ref: dir:.`. Every markdown file under `docs/`
 * is served by TechDocs against that entity. Pages are addressable by the
 * markdown file's path (without `.md`):
 *
 *   docs/blueprints/authoring-guide.md
 *     → path "blueprints/authoring-guide"
 *     → URL  /docs/default/component/backstage/blueprints/authoring-guide
 *
 * For docs owned by other entities (a service repo with its own `catalog-info.yaml`
 * and `docs/`), pass `entityRef` explicitly.
 *
 * ## Examples
 *
 * Inline a single page:
 *
 *   <EmbeddedDoc path="blueprints/authoring-guide" />
 *
 * Inline a page from another entity's docs:
 *
 *   <EmbeddedDoc
 *     path="how-to/incident-response"
 *     entityRef={{ kind: 'Component', namespace: 'default', name: 'imt-shield' }}
 *   />
 *
 * Show the in-page docs search bar (e.g. for a focused docs tab):
 *
 *   <EmbeddedDoc path="how-to/local-setup-runbook" withSearch />
 *
 * ## Common consumers (existing and planned)
 *
 *   - Blueprints → Authoring Guide tab          → docs/blueprints/authoring-guide.md
 *   - Devboxes plugin → Setup runbook panel     → docs/how-to/local-setup-runbook.md
 *   - Versions manager → Integration guide tab  → docs/how-to/versions-manager-integration.md
 *   - Catalog → Examples tab on certain entities → docs/examples/*
 *
 * Add new pages to `docs/` and reference them with `<EmbeddedDoc path="..." />`
 * — no extra wiring required. mkdocs auto-discovers the file on next build.
 */

import {
  TechDocsReaderPage,
  TechDocsReaderPageContent,
} from '@backstage/plugin-techdocs';
import type { CompoundEntityRef } from '@backstage/catalog-model';

/**
 * Default entity that owns docs in this Backstage repo.
 *
 * Mirrors `component:default/backstage` from the repo's root `catalog-info.yaml`.
 * Exported so other consumers (tests, link generators) can use the same constant.
 */
export const LOCAL_DOCS_ENTITY: CompoundEntityRef = {
  kind: 'Component',
  namespace: 'default',
  name: 'backstage',
};

export interface EmbeddedDocProps {
  /**
   * Path within the docs site, without leading slash and without `.md`.
   * Examples: `"blueprints/authoring-guide"`, `"how-to/local-setup-runbook"`.
   *
   * A leading slash is added internally — `<TechDocsReaderPageContent>` requires
   * `defaultPath` to start with `/` or the path is concatenated to the route URL
   * without a separator (e.g. `/create/guide` + `blueprints/...` becomes
   * `/create/guideblueprints/...`).
   */
  path: string;

  /**
   * Entity whose `backstage.io/techdocs-ref` annotation owns these docs.
   * Defaults to `component:default/backstage` (the repo root) so pages under
   * `docs/` in this repo work without extra props.
   */
  entityRef?: CompoundEntityRef;

  /**
   * Whether to render the in-page docs search bar. Default `false` — most
   * embeds are scoped to a specific page and an extra search bar duplicates
   * the global Backstage search. Set `true` for full-docs surfaces.
   */
  withSearch?: boolean;

  /**
   * Optional URL mapper for in-page search results. Only relevant when
   * `withSearch` is true. Useful when the embed lives at a route different
   * from `/docs/...` and you want search results to navigate within that
   * mounted view rather than to the global docs route.
   */
  searchResultUrlMapper?: (url: string) => string;
}

/**
 * Renders a single TechDocs page inline.
 *
 * Avoids the default reader chrome (page header / breadcrumbs) so callers can
 * compose freely with their own page layout. Loading / not-found states and
 * markdown rendering are handled by `<TechDocsReaderPageContent>`.
 */
export default function EmbeddedDoc({
  path,
  entityRef = LOCAL_DOCS_ENTITY,
  withSearch = false,
  searchResultUrlMapper,
}: EmbeddedDocProps): JSX.Element {
  // Normalise: TechDocsReaderPageContent's `defaultPath` is appended to the
  // current pathname using simple string concatenation. Without a leading
  // slash it produces malformed URLs like /create/guideblueprints/...
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;

  return (
    <TechDocsReaderPage entityRef={entityRef}>
      {/*
         Replace the default reader layout with just the rendered markdown
         body. Callers provide their own page chrome (tabs, headers).
      */}
      {() => (
        <TechDocsReaderPageContent
          defaultPath={normalisedPath}
          withSearch={withSearch}
          searchResultUrlMapper={searchResultUrlMapper}
        />
      )}
    </TechDocsReaderPage>
  );
}
